<script setup lang="ts">
import RcDisplaySerialNumber from '@/components/rc/common/RcDisplaySerialNumber.vue'
import PartStatusIcon from '@/components/rc/PartStatusIcon.vue'
import { useMinerDetails } from '@/composables/rc/miner-details'
import { usePartDetails } from '@/composables/rc/part-details'
import type { IDetailsPart, IMinerDetails } from '@/types/rc/miners'
import { PartStatus } from '@/types/rc/miners/PartStatus'
import { IndividualPart, partIcons, PartType, partTypes } from '@/types/rc/miners/PartType'
import type { PartState } from '@/types/rc/work-orders'
import { generateModelDisplayName } from '@/util/rc'
import { Icon } from '@iconify/vue'
import { computed, toRefs } from 'vue'

const emit = defineEmits([
  'openDrawer',
  'deletePart',
  'moveToInventory',
  'moveBackFromInventory',
  'openSparePartSelectionDialog',
])

const props = defineProps<{
  minerId: number
  selected: boolean
  part: IndividualPart
}>()

const { minerId, part } = toRefs(props)

const { minerDetails } = useMinerDetails(minerId)

// Set a reactive binding to props before passing it to composable
const { partDetails, isRecurrent, movedToInventory } = usePartDetails(minerId, part)

const minerCardElement = computed(() => (part.value === IndividualPart.Miner ? minerDetails.value! : partDetails.value))

const icon = computed(() => partIcons[partTypes[part.value]])

const toPartState = (element?: IDetailsPart | IMinerDetails): PartState | null => {
  if (!element) return null

  const part = element as IDetailsPart

  return { status: part.status, failure_type: part.failure?.failure_type.description ?? null }
}

const determineModelName = (part: IndividualPart, element: IDetailsPart | IMinerDetails | null): string => {
  if (!element) return ''
  if (part == IndividualPart.Miner) return element?.model ?? ''

  const detailPart = element as IDetailsPart

  return generateModelDisplayName(detailPart.model ?? '', detailPart.model_version)
}

const showDeleteButton = (part: IMinerDetails | IDetailsPart | null): boolean => {
  if (!part) return false

  // If the part type is Miner, return false (this is reserved for actual parts)
  if (partTypes[props.part] == PartType.Miner) return false

  const partData = part as IDetailsPart

  // If the part has a SN, a failure or it has been replaced, it cannot be deleted
  if (partData.sn || partData.failure || partData.replacement_spare_part) return false

  return true
}

const showMoveToInventoryButton = (part: IMinerDetails | IDetailsPart | null): boolean => {
  if (!part) return false

  // If the part type is Miner, return false (this is reserved for actual parts)
  if (partTypes[props.part] === PartType.Miner) return false

  const partData = part as IDetailsPart

  /* In order for a part to be moved to the inventory
  - It must have a SN and a model
  - It must not be a replacement
  - It must not have been moved to the inventory
  - It must have a status different from "Blocked"
  */
  if (
    !partData.sn ||
    !partData.model ||
    partData.replacement_spare_part ||
    partData.moved_to_inventory ||
    !partData.status ||
    partData.status === PartStatus.Blocked
  )
    return false

  return true
}

const handlePartDelete = (part: IDetailsPart) => {
  emit('deletePart', part.id, partTypes[props.part])
}

const handlePartMoveToInventory = (part: IDetailsPart) => {
  emit('moveToInventory', part.id, partTypes[props.part])
}

const handlePartMoveBackFromInventory = (part: IDetailsPart) => {
  emit('moveBackFromInventory', part.id, partTypes[props.part])
}
</script>

<template>
  <div
    :class="['card__body', { selected, 'moved-to-inventory': movedToInventory }]"
    @click="() => emit('openDrawer', part)"
  >
    <div class="card__body__name-section">
      <Icon :icon="icon" />
      <span class="card__body__value">{{ part }}</span>
      <el-tooltip v-if="movedToInventory" effect="dark" content="Moved to inventory" placement="top-start">
        <Icon icon="material-symbols:shelves" :width="20" />
      </el-tooltip>
    </div>
    <div class="card__body__model-section">
      <span class="card__body__value">{{ determineModelName(part, minerCardElement) }}</span>
    </div>
    <div class="card__body__sn-section">
      <span class="card__body__value">
        <RcDisplaySerialNumber
          v-if="minerCardElement?.sn"
          :sn="minerCardElement.sn"
          :threshold="24"
          :recurrent="isRecurrent"
          :type="part === IndividualPart.Miner ? 'miner' : 'part'"
        />
      </span>
    </div>
    <div class="card__body__status-section">
      <part-status-icon
        v-if="partTypes[part] != PartType.Miner"
        tooltip-placement="right"
        :part-state="toPartState(minerCardElement ?? undefined) ?? undefined"
      />
      <el-button
        v-if="showDeleteButton(minerCardElement)"
        link
        title="Mark as missing"
        @click="handlePartDelete(minerCardElement as IDetailsPart)"
        ><Icon icon="material-symbols:help-outline" height="20"></Icon
      ></el-button>
      <el-button
        v-if="showMoveToInventoryButton(minerCardElement)"
        link
        title="Move to inventory"
        @click.stop="handlePartMoveToInventory(minerCardElement as IDetailsPart)"
        ><Icon icon="material-symbols:move-down" height="20"></Icon
      ></el-button>
      <el-button
        v-if="movedToInventory"
        link
        title="Move back from inventory"
        @click.stop="handlePartMoveBackFromInventory(minerCardElement as IDetailsPart)"
        ><Icon icon="material-symbols:move-up" height="20"></Icon
      ></el-button>
      <el-button v-if="!minerCardElement" link title="Attach part" @click="emit('openSparePartSelectionDialog')"
        ><Icon icon="material-symbols:add" height="20"></Icon
      ></el-button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.card {
  &__body {
    display: flex;
    flex-direction: row;
    background: var(--fill-secondary);
    border-radius: 6px;
    min-width: max-content;

    &.selected {
      border: 1px solid var(--component-stroke-gray);
    }

    &.moved-to-inventory {
      background: var(--component-fill-manz);
      position: relative;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 6px;
        background: var(--text-badge-manz);
        border-radius: 6px 0 0 6px;
      }

      &:hover,
      &.selected {
        background: var(--component-fill-manz-header);
      }
    }

    &:hover,
    &.selected {
      background-color: var(--fill-tertiary);
    }

    &__name-section {
      display: flex;
      width: 20%;
      min-width: 80px;
      gap: var(--spacing-spacing-xs);
      padding: var(--spacing-spacing-sm);
      align-items: center;
    }

    &__model-section {
      display: flex;
      width: 30%;
      min-width: 120px;
      gap: var(--spacing-spacing-xs);
      padding: var(--spacing-spacing-sm);
      align-items: center;
    }

    &__sn-section {
      display: flex;
      width: 35%;
      min-width: 180px;
      gap: var(--spacing-spacing-xs);
      padding: var(--spacing-spacing-sm);
      align-items: center;
    }

    &__status-section {
      display: flex;
      width: 15%;
      min-width: 80px;
      gap: var(--spacing-spacing-xs);
      padding: var(--spacing-spacing-sm);
      align-items: center;
    }

    &__value {
      color: var(--text-primary);
      font-size: var(--font-size-body-compact);
      font-weight: 400;
      line-height: 20px;
    }
  }
}
</style>
